import { useCallback, useState } from "react";
import { customFetch } from "@workspace/api-client-react";

/**
 * Three-step direct-to-R2 upload:
 *   1. POST /files/upload-url → presigned PUT + fileId
 *   2. PUT bytes to R2 (presigned URL — no auth, no CORS issue)
 *   3. POST /files/{fileId}/finalize → server verifies size + sha256
 *
 * SHA256 is computed in the browser via SubtleCrypto.
 */

export type UploadKind =
  | "rfp_source"
  | "required_doc"
  | "attachment"
  | "brand_guide"
  | "client_doc";

const ALLOWED_MIME = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png",
  "image/jpeg",
]);

const MAX_BYTES = 10 * 1024 * 1024;

interface UploadUrlResponse {
  fileId: string;
  uploadUrl: string;
  expiresAt: string;
}

interface FinalizeResponse {
  id: string;
  kind: string;
  filename: string;
  contentType: string;
  byteSize: number;
  sha256: string;
  status: "pending" | "ready";
  createdAt: string;
}

export interface UseFileUploadOptions {
  kind: UploadKind;
  projectId?: string;
}

export interface UploadProgress {
  status: "idle" | "hashing" | "presigning" | "uploading" | "finalizing" | "done" | "error";
  pct: number;
  fileId: string | null;
  error: string | null;
}

export function useFileUpload(opts: UseFileUploadOptions) {
  const [progress, setProgress] = useState<UploadProgress>({
    status: "idle",
    pct: 0,
    fileId: null,
    error: null,
  });

  const upload = useCallback(
    async (file: File): Promise<FinalizeResponse> => {
      // Up-front validation that mirrors the server.
      if (file.size > MAX_BYTES) {
        const err = "File is larger than the 10 MB limit.";
        setProgress({ status: "error", pct: 0, fileId: null, error: err });
        throw new Error(err);
      }
      if (!ALLOWED_MIME.has(file.type)) {
        const err = `Unsupported file type: ${file.type || "unknown"}`;
        setProgress({ status: "error", pct: 0, fileId: null, error: err });
        throw new Error(err);
      }

      try {
        // 1. Hash locally so we can hand the digest to /finalize.
        setProgress({ status: "hashing", pct: 5, fileId: null, error: null });
        const sha256 = await sha256Hex(file);

        // 2. Ask the API for a presigned PUT URL.
        setProgress((p) => ({ ...p, status: "presigning", pct: 15 }));
        const { fileId, uploadUrl } = await customFetch<UploadUrlResponse>(
          "/api/v1/files/upload-url",
          {
            method: "POST",
            body: JSON.stringify({
              filename: file.name,
              contentType: file.type,
              byteSize: file.size,
              kind: opts.kind,
              projectId: opts.projectId ?? null,
            }),
          },
        );

        // 3. PUT bytes directly to R2.
        setProgress({ status: "uploading", pct: 25, fileId, error: null });
        await putToR2(uploadUrl, file, (pct) =>
          setProgress((p) => ({ ...p, pct: 25 + Math.round(pct * 0.65) })),
        );

        // 4. Finalize.
        setProgress((p) => ({ ...p, status: "finalizing", pct: 92 }));
        const final = await customFetch<FinalizeResponse>(`/api/v1/files/${fileId}/finalize`, {
          method: "POST",
          body: JSON.stringify({ sha256 }),
        });

        setProgress({ status: "done", pct: 100, fileId, error: null });
        return final;
      } catch (err) {
        const message = (err as Error).message ?? "Upload failed.";
        setProgress((p) => ({ ...p, status: "error", error: message }));
        throw err;
      }
    },
    [opts.kind, opts.projectId],
  );

  const reset = useCallback(() => {
    setProgress({ status: "idle", pct: 0, fileId: null, error: null });
  }, []);

  return { upload, progress, reset };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function sha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Use XMLHttpRequest because fetch doesn't expose upload progress events
 * in any browser as of 2026 (ReadableStream uploads are still gated).
 */
function putToR2(url: string, file: File, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else
        reject(
          new Error(`Couldn't upload the file (${xhr.status}). Check your file storage settings.`),
        );
    };
    xhr.onerror = () =>
      reject(
        new Error("Couldn't reach the file storage service. Check the bucket's CORS settings."),
      );
    xhr.send(file);
  });
}
